/**
 * Keskitetyt vakiot ja tyypit koko sovellukselle
 * 
 * Käyttö:
 * import { ORDER_STATUS_LABELS, INSTALLATION_STATUS_LABELS } from "@/config/constants";
 */

// ============================================================================
// TILAUKSET (Orders)
// ============================================================================

export const ORDER_STATUS_LABELS = {
  draft: "Luonnos",
  confirmed: "Vahvistettu",
  ready_for_installation: "Valmis asennukseen",
  installed: "Asennettu",
  completed: "Valmis",
  cancelled: "Peruutettu",
} as const;

export const ORDER_STATUS_COLORS = {
  draft: "bg-muted text-muted-foreground",
  confirmed: "bg-primary text-primary-foreground",
  ready_for_installation: "bg-secondary text-secondary-foreground",
  installed: "bg-primary text-primary-foreground",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive text-destructive-foreground",
} as const;

export const ORDER_TYPE_LABELS = {
  new_work: "Uusi työ",
  pickup_work: "Noudettava",
  field_work: "Kenttätyö",
} as const;

export const ORDER_TYPE_COLORS = {
  new_work: "bg-primary/10 text-primary border-primary/20",
  pickup_work: "bg-orange-500/10 text-orange-700 border-orange-500/20",
  field_work: "bg-purple-500/10 text-purple-700 border-purple-500/20",
} as const;

export const FULFILLMENT_TYPE_LABELS = {
  delivery: "Toimitus",
  installation: "Asennus",
  pickup: "Nouto",
  return: "Palautus",
} as const;

export const FULFILLMENT_TYPE_COLORS = {
  delivery: "bg-blue-500/10 text-blue-700 border-blue-500/20",
  installation: "bg-green-500/10 text-green-700 border-green-500/20",
  pickup: "bg-red-500/10 text-red-700 border-red-500/20",
  return: "bg-yellow-500/10 text-yellow-700 border-yellow-500/20",
} as const;

// ============================================================================
// ASENNUKSET (Installations)
// ============================================================================

export const INSTALLATION_STATUS_LABELS = {
  pending: "Odottaa",
  in_progress: "Käynnissä",
  completed: "Valmis",
  cancelled: "Peruutettu",
  partial: "Osittain",
  skipped: "Ohitettu",
} as const;

export const INSTALLATION_STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  partial: "bg-orange-100 text-orange-800",
  skipped: "bg-gray-100 text-gray-800",
} as const;

// Badge-versiot (käytä Badge-komponentissa)
export const INSTALLATION_STATUS_BADGE_COLORS = {
  pending: "bg-yellow-500",
  in_progress: "bg-blue-500",
  completed: "bg-green-500",
  cancelled: "bg-red-500",
  partial: "bg-orange-500",
  skipped: "bg-gray-500",
} as const;

export const TASK_TYPE_LABELS = {
  installation: "Asennus",
  delivery: "Toimitus",
  maintenance: "Huolto",
  pickup: "Nouto",
} as const;

// ============================================================================
// KUORMAT (Loads)
// ============================================================================

export const LOAD_STATUS_LABELS = {
  planned: "Suunniteltu",
  in_progress: "Käynnissä",
  completed: "Valmis",
  cancelled: "Peruutettu",
} as const;

export const LOAD_STATUS_COLORS = {
  planned: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
} as const;

// ============================================================================
// VEDOKSET (Proofs)
// ============================================================================

export const PROOF_STATUS_LABELS = {
  pending: "Odottaa",
  sent: "Lähetetty",
  approved: "Hyväksytty",
  revision_requested: "Korjattava",
} as const;

export const PROOF_STATUS_COLORS = {
  pending: "bg-muted text-muted-foreground",
  sent: "bg-blue-500/10 text-blue-700",
  approved: "bg-green-500/10 text-green-700",
  revision_requested: "bg-orange-500/10 text-orange-700",
} as const;

// ============================================================================
// HAUTAUSMAAN HYVÄKSYNTÄ (Cemetery Approval)
// ============================================================================

export const CEMETERY_APPROVAL_STATUS_LABELS = {
  not_required: "Ei vaadita",
  awaiting_request: "Odottaa lähettämistä",
  pending: "Odottaa vastausta",
  approved: "Hyväksytty",
  rejected: "Hylätty",
} as const;

export const CEMETERY_APPROVAL_STATUS_COLORS = {
  not_required: "bg-muted text-muted-foreground",
  awaiting_request: "bg-blue-500/10 text-blue-700 border-blue-500/20",
  pending: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  approved: "bg-green-500/10 text-green-700 border-green-500/20",
  rejected: "bg-red-500/10 text-red-700 border-red-500/20",
} as const;

// ============================================================================
// VIESTINTÄ (Communication)
// ============================================================================

export const COMMUNICATION_TYPE_LABELS = {
  email: "Sähköposti",
  phone: "Puhelu",
  sms: "SMS",
  note: "Muistiinpano",
  system: "Järjestelmä",
} as const;

export const COMMUNICATION_DIRECTION_LABELS = {
  inbound: "Saapuva",
  outbound: "Lähtevä",
} as const;

export const COMMUNICATION_STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
} as const;

// ============================================================================
// ASIAKASTYYPIT (Customer Types)
// ============================================================================

export const CUSTOMER_TYPE_LABELS = {
  private: "Yksityishenkilö",
  company: "Yritys",
  municipality: "Kunta",
  estate: "Kuolinpesä",
} as const;

// ============================================================================
// KUMPPANITYYPPIT (Partner Types)
// ============================================================================

export const PARTNER_TYPE_LABELS = {
  installer: "Asentaja",
  workshop: "Työpaja",
} as const;

// ============================================================================
// MATERIAALIT (Materials)
// ============================================================================

export const MATERIAL_UNIT_LABELS = {
  piece: "kpl",
  m2: "m²",
  m: "m",
  kg: "kg",
  l: "l",
} as const;

// ============================================================================
// ALV (VAT)
// ============================================================================

export const DEFAULT_VAT_RATE = 25.5;

export const VAT_RATE_OPTIONS = [
  { value: 25.5, label: "25,5 % (yleinen)" },
  { value: 14, label: "14 % (ruoka)" },
  { value: 10, label: "10 % (kirjat, lääkkeet)" },
  { value: 0, label: "0 % (veroton)" },
] as const;

// ============================================================================
// TYYPPI-APURIT
// ============================================================================

export type OrderStatus = keyof typeof ORDER_STATUS_LABELS;
export type OrderType = keyof typeof ORDER_TYPE_LABELS;
export type FulfillmentType = keyof typeof FULFILLMENT_TYPE_LABELS;
export type InstallationStatus = keyof typeof INSTALLATION_STATUS_LABELS;
export type LoadStatus = keyof typeof LOAD_STATUS_LABELS;
export type ProofStatus = keyof typeof PROOF_STATUS_LABELS;
export type CemeteryApprovalStatus = keyof typeof CEMETERY_APPROVAL_STATUS_LABELS;
export type CommunicationType = keyof typeof COMMUNICATION_TYPE_LABELS;
export type CustomerType = keyof typeof CUSTOMER_TYPE_LABELS;
export type PartnerType = keyof typeof PARTNER_TYPE_LABELS;

// ============================================================================
// APUFUNKTIOT STATUS-ARVOILLE
// ============================================================================

/**
 * Hae tilauksen statuksen nimi
 */
export function getOrderStatusLabel(status: string): string {
  return ORDER_STATUS_LABELS[status as OrderStatus] || status;
}

/**
 * Hae asennuksen statuksen nimi
 */
export function getInstallationStatusLabel(status: string): string {
  return INSTALLATION_STATUS_LABELS[status as InstallationStatus] || status;
}

/**
 * Hae kuorman statuksen nimi
 */
export function getLoadStatusLabel(status: string): string {
  return LOAD_STATUS_LABELS[status as LoadStatus] || status;
}

/**
 * Hae vedoksen statuksen nimi
 */
export function getProofStatusLabel(status: string): string {
  return PROOF_STATUS_LABELS[status as ProofStatus] || status;
}
